/**
 * 初始化结账流程
 * 1. 检查URL参数checkoutDetail
 * 2. 解密订单信息
 * 3. 获取订单详情
 */
// 导入获取订单信息的API
import { getOrderInfo_checkout, getFieldConfig_checkout, getStatus } from '@/api/checkout';
// 导入日志记录API
import { addLog } from '@/api/common';
// 导入路由实例
import router from '@/router';
// 导入工具函数
import { initCardPaymentFields } from '@/utils/tools/cardPaymentBasicForm';
import { unzipOrder } from '@/utils/tools/utils';

import { CHECKOUT_STATUS, exceptionShow, CheckoutException } from '../config';

/**
 * 初始化方法，获取完整订单信息
 * @param {Object} context - Vue组件上下文，包含route, key等信息
 * @param {Function} setOrderCallback - 设置订单信息的回调函数
 * @param {Function} setLoadingCallback - 设置加载状态的回调函数
 * @returns {Promise} 初始化处理的Promise
 */
export const initCheckoutProcess = async (context, setOrderCallback, setLoadingCallback) => {
  try {
    // 从上下文中解构出路由和key信息
    const { route, key } = context;

    // 记录开始请求的时间
    context.requestingTime = Date.now();

    // 1. 检查URL参数checkoutDetail
    const checkoutDetail = route.query.checkoutDetail;
    if (checkoutDetail) {
      // 设置请求时间为-1，表示使用本地数据
      context.requestingTime = -1;

      // 2. 解密订单信息
      const info = unzipOrder(checkoutDetail);
      if (info) {
        // 初始化卡支付字段
        const orderInfo = initCardPaymentFields(info);

        // 处理订单信息
        return handleOrderInfo(orderInfo, context, setOrderCallback, setLoadingCallback);
      } else {
        try {
          // 记录解密失败的日志
          addLog({
            key,
            message: '解密失败:' + checkoutDetail,
          });
        } catch (err) {
          // 记录日志失败时的错误处理
          console.error('记录日志失败:', err);
        }
      }
    }

    // 3. 获取完整订单详情（初始化）
    const res = await getOrderInfo_checkout({
      key,
      country: route.query.c,
    });

    // 检查API响应状态
    if (res.respCode !== '20000') {
      // 处理异常情况
      if (exceptionShow(res)) {
        return;
      }

      // 跳转到无效订单页面
      // TODO
      // 跳到独立页
      throw new CheckoutException({
        status: CHECKOUT_STATUS.PENDING,
        message: '',
      });
      // this.$router.push({
      //   path: '/result/pending',
      //   query: { key: this.$route.query.key ? this.$route.query.key : this.$route.params.id, type: '1' },
      // });
      // return;
    }

    // 处理订单信息
    return handleOrderInfo(res.data, context, setOrderCallback, setLoadingCallback);
  } catch (error) {
    // 捕获并记录所有错误
    throw new CheckoutException({
      status: CHECKOUT_STATUS.ERROR,
      message: '',
    });
    // 出错时的处理
    // try {
    //   // 发生错误时跳转到无效订单页面
    //   router.push({
    //     path: '/result/invalid',
    //     query: {
    //       key: context.route.query.key ? context.route.query.key : context.route.params.id,
    //       type: '1',
    //     },
    //   });
    // } catch (routerError) {
    //   // 路由跳转失败时的错误处理
    //   console.error('路由跳转失败:', routerError);
    // }
    // return {
    //   success: false,
    //   message: error.message,
    // };
  }
};

/**
 * 重新渲染方法，只更新订单的部分数据
 * @param {Object} context - Vue组件上下文，包含route, key等信息
 * @param {Object} currentOrder - 当前订单信息
 * @param {Function} updateOrderCallback - 更新订单信息的回调函数
 * @param {Function} setLoadingCallback - 设置加载状态的回调函数
 * @param {Object} params - 请求参数，包含如cardNumber, country等
 * @returns {Promise} 重新渲染处理的Promise
 */
export const rerenderCheckout = async (context, currentOrder, updateOrderCallback, setLoadingCallback, params) => {
  try {
    // 从上下文中解构出key信息
    const { key } = context;

    // 记录日志
    try {
      addLog({
        key,
        message: '页面重新渲染，参数:' + JSON.stringify(params),
      });
    } catch (err) {
      console.error('记录日志失败:', err);
    }

    // 获取字段配置信息（重新渲染）
    const res = await getFieldConfig_checkout({
      key,
      ...params,
    });

    // 检查API响应状态
    if (res.respCode !== '20000') {
      // 处理异常情况
      if (exceptionShow(res)) {
        return;
      }

      // 返回失败信息，让调用方自行处理
      return {
        success: false,
        code: res.respCode,
        message: res.respMsg,
      };
    }

    // 更新订单的特定字段，而不是整个订单
    const updatedOrder = {
      ...currentOrder,
      // 更新卡类型信息
      cardTypeInfo: res.data.cardTypeInfo,
      // 更新支付配置字段
      paymentConfigurationFields: res.data.paymentConfigurationFields,
      // 更新账单配置字段
      billConfigurationFields: res.data.billConfigurationFields,
      // 更新分期信息
      canInstallment: res.data.canInstallment,
    };

    // 分期选项由PaymentForm组件内部处理，不再在此处处理

    // 更新订单信息
    updateOrderCallback(updatedOrder);
    // 关闭加载状态
    setLoadingCallback(false);

    return {
      success: true,
      data: updatedOrder,
    };
  } catch (error) {
    console.error('重新渲染结账流程出错:', error);

    throw new CheckoutException({
      status: CHECKOUT_STATUS.ERROR,
      message: '',
    });
  }
};

/**
 * 处理订单信息，判断订单状态并执行相应操作
 * @param {Object} orderInfo - 订单信息
 * @param {Object} ctx - 上下文
 * @param {Function} setOrder - 设置订单回调
 * @param {Function} setLoading - 设置加载状态回调
 */
function handleOrderInfo(orderInfo, ctx, setOrder, setLoading) {
  // 从上下文中解构出路由信息
  const { route } = ctx;
  // TODO paymentCountry
  // if (res.data.paymentCountry) {
  //   this.form.country = res.data.paymentCountry;
  // }
  // 检查订单状态
  if (orderInfo.status === 'F') { // F跳转，失败页面
    // 订单失败时跳转到失败页面
    throw new CheckoutException({
      status: CHECKOUT_STATUS.PENDING,
      message: '',
    });
    // this.$router.push({
    //   path: '/result/pending',
    //   query: { key: this.$route.query.key ? this.$route.query.key : this.$route.params.id, type: '1' },
    // });
    // return;
  }

  // 处理shopify特殊情况
  if (orderInfo.newCheckout) {
    // 如果有新的结账URL，直接跳转
    window.location.href = orderInfo.newCheckout;
    return;
  }

  // 检查支付国家 TODO 确认是否需要保留
  if (!orderInfo.paymentCountry) {
    // 没有支付国家时跳转到地址选择页面
    router.push({
      path: '/address/s',
      query: { key: route.query.key ? route.query.key : route.params.id, type: '1' },
    });
    return;
  }

  return orderInfo;
}

/**
 * 获取订单状态，并在pending状态下进行轮询刷新
 * @param {Object} context - Vue组件上下文，包含route, key等信息
 * @returns {Promise} 获取订单状态的Promise
 */
export const getOrderStatus = async (context) => {
  // 从上下文中解构出路由和key信息
  const { key } = context;
  // 初始化重试计数器
  let tryCount = 0;
  // 设置重试间隔时间（毫秒）
  const tryInterval = 1000;
  // 设置最大重试次数
  const pending = 16;

  // 定义获取订单状态的函数
  const fetchStatus = async () => {
    // 增加重试计数
    tryCount += 1;
    // 调用API获取订单状态
    const res = await getStatus(key).catch((error) => { res = {}; });
    // 检查API响应状态
    if (res.respCode === '20000') {
      // 获取订单信息
      const orderInfo = res.data;
      // 检查订单状态
      if (orderInfo.status === 'S') { // 成功
        // 返回成功状态
        throw new CheckoutException({
          status: CHECKOUT_STATUS.SUCCESS,
          message: '',
        });
      } else if (orderInfo.status === 'F') { // 失败
        // 返回失败状态
        throw new CheckoutException({
          status: CHECKOUT_STATUS.FAILED,
          message: orderInfo?.reason || res?.respMsg,
          others: orderInfo,
        });
      } else if (orderInfo.status === 'P' || orderInfo.status === 'R') { // 等待/重定向
        // 如果未达到最大重试次数，继续重试
        if (tryCount < pending) { // 重试
          // 使用Promise包装setTimeout，确保异常能被正确捕获
          await sleep(tryInterval);
          return await fetchStatus();
        } else {
          // 达到最大重试次数，返回当前状态
          throw new CheckoutException({
            status: CHECKOUT_STATUS.ERROR,
            message: '',
          });
          // return orderInfo;
        }
      } else {
        // 其他状态，直接返回
        return orderInfo;
      }
    } else {
      // 处理API响应异常
      if (res.data?.retry === '1') {
        // 如果需要重试，跳转到聚合页面
        router.push({
          path: '/aggregate',
          query: { key },
        });
      }
      // 处理特定错误码 TODO 50054为什么要重新刷新
      if (res.respCode === '00020|50054' || res.respCode === '50054') {
        // 如果未达到最大重试次数，继续重试
        if (tryCount < pending) {
          await sleep(tryInterval);
          return await fetchStatus();
        } else {
          // 达到最大重试次数，返回当前状态
          throw new CheckoutException({
            status: CHECKOUT_STATUS.ERROR,
            message: '',
          });
          // return res.data;
        }
      }
      // 处理其他异常情况,不能再触发getOrderStatus
      exceptionShow(res, true);
      // 返回当前状态
      if (tryCount < pending) { // 重试
        await sleep(tryInterval);
        return await fetchStatus();
      } else {
        throw new CheckoutException({
          status: CHECKOUT_STATUS.ERROR,
          message: '',
        });
      }
    }
  };

  // 返回获取订单状态的结果
  return await fetchStatus();
};
// 写一个sleep方法
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 导出模块
export default {
  initCheckoutProcess,
  rerenderCheckout,
  getOrderStatus,
};
